/**
 * Product Select - Vehicle Finder Tool
 * Allows users to select their vehicle and find compatible chargers
 */

class ProductSelect {
  constructor() {
    this.vehicles = [];
    this.products = [];
    this.selectedVehicle = null;
    this.selectedProduct = null;
    this.config = {};
    this.translations = {};
    
    // DOM elements
    this.yearSelect = null;
    this.modelSelect = null;
    this.productsGrid = null;
    
    this.init();
  }

  async init() {
    try {
      console.log('🚗 Initializing Product Select...');
      
      // Load config and translations
      this.loadConfig();
      
      // Cache DOM elements
      this.cacheElements();
      
      // Load data
      await this.loadVehicles();
      this.loadProducts();
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Initialize dropdowns
      this.buildYearOptions();
      
      console.log('✅ Product Select initialized successfully');
      
    } catch (error) {
      console.error('❌ Error initializing Product Select:', error);
    }
  }

  loadConfig() {
    try {
      const configEl = document.getElementById('product-select-config');
      if (configEl && configEl.textContent) {
        this.config = JSON.parse(configEl.textContent);
        this.translations = this.config.translations || {};
        console.log('✅ Loaded config and translations:', this.translations);
      }
    } catch (error) {
      console.error('❌ Error loading config:', error);
      this.translations = {};
    }
  }

  cacheElements() {
    this.yearSelect = document.getElementById('vehicle-year');
    this.modelSelect = document.getElementById('vehicle-model');
    this.productsGrid = document.getElementById('products-grid');
    this.compatibleSection = document.getElementById('compatible-products');
  }

  setupEventListeners() {
    this.yearSelect?.addEventListener('change', () => this.handleYearChange());
    this.modelSelect?.addEventListener('change', () => this.handleModelChange());
  }

  async loadVehicles() {
    try {
      console.log('🚗 Loading vehicle data...');
      
      const query = `
        query getVehicles($type: String!, $first: Int!) {
          metaobjects(type: $type, first: $first) {
            edges {
              node {
                id
                fields {
                  key
                  value
                }
                handle
              }
            }
          }
        }
      `;

      const variables = {
        "type": "vehicle",
        "first": 250
      };

      const response = await fetch(window.location.origin + '/api/2023-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '9b3e57031bb9c33684705d8f196178b7'
        },
        body: JSON.stringify({ 
          query: query,
          variables: variables 
        })
      });

      if (!response.ok) {
        throw new Error('HTTP error! status: ' + response.status);
      }

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        throw new Error('GraphQL query failed');
      }

      if (!data.data?.metaobjects?.edges) {
        throw new Error('No vehicle data in response');
      }

      // Process the vehicle data
      this.vehicles = data.data.metaobjects.edges.map(edge => {
        const vehicleNode = edge.node;
        const fields = vehicleNode.fields.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {});

        return {
          year: parseInt(fields['vehicle.year'] || fields.year),
          model: fields['vehicle.model'] || fields.model,
          default_charger: fields['vehicle.default_charger'] || fields.default_charger
        };
      });

      console.log('✅ Loaded ' + this.vehicles.length + ' vehicles:', this.vehicles);
      
    } catch (error) {
      console.error('❌ Error loading vehicles:', error);
      this.vehicles = [];
    }
  }

  loadProducts() {
    try {
      const productsDataEl = document.getElementById('charger-products-data');
      if (productsDataEl && productsDataEl.textContent) {
        this.products = JSON.parse(productsDataEl.textContent);
        console.log('✅ Loaded ' + this.products.length + ' products:', this.products);
        
        // Debug: Show charger types
        const chargerTypes = this.products.map(p => p.charger_type).filter(Boolean);
        console.log('🔌 Product charger types found:', [...new Set(chargerTypes)]);
      }
    } catch (error) {
      console.error('❌ Error loading products:', error);
      this.products = [];
    }
  }

  buildYearOptions() {
    if (!this.yearSelect || !this.vehicles.length) return;

    const uniqueYears = [...new Set(this.vehicles.map(v => v.year))]
      .filter(year => year && !isNaN(year))
      .sort((a, b) => b - a); // Newest first

    this.yearSelect.innerHTML = `<option value="">${this.translations.select_year || 'Select Year'}</option>`;
    uniqueYears.forEach(year => {
      const option = new Option(year.toString(), year.toString());
      this.yearSelect.appendChild(option);
    });

    this.yearSelect.disabled = false;
    console.log('✅ Built year options:', uniqueYears);
  }

  handleYearChange() {
    const selectedYear = this.yearSelect.value;
    
    // Reset dependent dropdowns
    this.modelSelect.innerHTML = `<option value="">${this.translations.select_model || 'Select Model'}</option>`;
    this.modelSelect.disabled = true;
    this.hideProducts();

    if (!selectedYear) return;

    // Build model options for selected year (no make field needed)
    const vehiclesForYear = this.vehicles.filter(v => v.year == selectedYear);
    const uniqueModels = [...new Set(vehiclesForYear.map(v => v.model))]
      .filter(model => model)
      .sort();

    uniqueModels.forEach(model => {
      const option = new Option(model, model);
      this.modelSelect.appendChild(option);
    });

    this.modelSelect.disabled = false;
    console.log('✅ Built model options for ' + selectedYear + ':', uniqueModels);
  }

  handleMakeChange() {
    // This method is no longer needed since we go directly from year to model
    // Keeping for compatibility but it does nothing
    return;
  }

  handleModelChange() {
    const selectedYear = this.yearSelect.value;
    const selectedModel = this.modelSelect.value;

    if (!selectedYear || !selectedModel) {
      this.hideProducts();
      return;
    }

    // Find the matching vehicle (just year and model, no make)
    this.selectedVehicle = this.vehicles.find(v => 
      v.year == selectedYear && 
      v.model === selectedModel
    );

    if (!this.selectedVehicle) {
      console.error('❌ Vehicle not found:', { selectedYear, selectedModel });
      return;
    }

    console.log('✅ Vehicle selected:', this.selectedVehicle);
    this.showCompatibleProducts();
  }

  showCompatibleProducts() {
    if (!this.selectedVehicle || !this.selectedVehicle.default_charger) {
      console.error('❌ No default charger for selected vehicle');
      return;
    }

    console.log('🔍 Filtering products for vehicle charger:', this.selectedVehicle.default_charger);
    console.log('🔍 Available products:', this.products.map(p => ({title: p.title, charger_type: p.charger_type})));

    // Filter products by charger type (include UNIVERSAL as backup)
    const compatibleProducts = this.products.filter(product => 
      product.charger_type === this.selectedVehicle.default_charger || 
      product.charger_type === 'UNIVERSAL'
    );

    console.log('🔌 Found ' + compatibleProducts.length + ' compatible products for ' + this.selectedVehicle.default_charger);

    if (compatibleProducts.length === 0) {
      this.productsGrid.innerHTML = '<p>No compatible chargers found for your vehicle.</p>';
    } else if (compatibleProducts.length === 1) {
      // Only one product - redirect immediately for better UX
      console.log('🎯 Only one compatible product - redirecting directly');
      window.location.href = compatibleProducts[0].url;
      return;
    } else {
      this.renderProducts(compatibleProducts);
    }

    this.compatibleSection.style.display = 'block';
  }

  renderProducts(products) {
    this.productsGrid.innerHTML = products.map(product => `
      <div class="product-card" data-product-url="${product.url}">
        <div class="product-card__image">
          ${product.featured_image ? 
            `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">` : 
            '<div class="product-card__placeholder">No Image</div>'
          }
        </div>
        <div class="product-card__details">
          <h3 class="product-card__title">${product.title}</h3>
          <p class="product-card__price">${product.price_formatted}</p>
        </div>
        <div class="product-card__action">
          <button class="product-card__select-btn" data-product-url="${product.url}">
            ${this.translations.select_button || 'Select'}
          </button>
        </div>
      </div>
    `).join('');

    // Add event listeners to select buttons
    const selectButtons = this.productsGrid.querySelectorAll('.product-card__select-btn');
    selectButtons.forEach(button => {
      button.addEventListener('click', () => {
        const productUrl = button.dataset.productUrl;
        
        // Add vehicle parameters to the URL
        if (this.selectedVehicle) {
          const url = new URL(productUrl, window.location.origin);
          url.searchParams.set('vehicle_year', this.selectedVehicle.year);
          url.searchParams.set('vehicle_model', this.selectedVehicle.model);
          
          console.log('🚀 Navigating to product with vehicle data:', {
            url: url.toString(),
            year: this.selectedVehicle.year,
            model: this.selectedVehicle.model
          });
          
          window.location.href = url.toString();
        } else {
          console.log('🚀 Navigating to product without vehicle data:', productUrl);
          window.location.href = productUrl;
        }
      });
    });
  }

  hideProducts() {
    this.compatibleSection.style.display = 'none';
    this.selectedProduct = null;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductSelect());
} else {
  new ProductSelect();
} 